import { useCallback, useEffect, useRef, useState } from 'react';
import type { Meeting } from '../../types';
import { useLanguage } from '../../lib/i18n';
import { dtoToMeeting, meetingToDto, relocalizeMeeting } from './mapMeeting';

interface MeetingsState {
  meetings: Meeting[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Toplantı deposunun arayüz tarafı.
 *
 * Önceden bu veri `INITIAL_MEETINGS` sabitinden geliyor ve yalnızca bellekte
 * yaşıyordu: uygulama kapanınca transkriptler, notlar ve aksiyon maddeleri
 * tamamen kayboluyordu. Artık kaynak Supabase.
 *
 * Yazma işlemleri iyimser (optimistic): arayüz anında güncelleniyor, sunucu
 * yazımı arkada ilerliyor. Yazma başarısız olursa hata yüzeye çıkıyor ve
 * liste sunucudan yeniden yükleniyor — ekranda uydurma bir durum kalmasın.
 */
export function useMeetings(enabled: boolean) {
  const [state, setState] = useState<MeetingsState>({
    meetings: [],
    isLoading: enabled,
    error: null,
  });

  const mountedRef = useRef(true);

  // Tarih ve süre metinleri ("Bugün", "40 dk") eşleme anında üretiliyor; dil
  // değişince bir kez yeniden üretilsin.
  const { language } = useLanguage();
  const languageRef = useRef(language);
  useEffect(() => {
    if (languageRef.current === language) return;
    languageRef.current = language;
    setState((prev) => ({ ...prev, meetings: prev.meetings.map(relocalizeMeeting) }));
  }, [language]);

  const load = useCallback(async () => {
    if (!enabled) {
      setState({ meetings: [], isLoading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const dtos = await window.zabit.listMeetings();
      if (!mountedRef.current) return;
      setState({ meetings: dtos.map(dtoToMeeting), isLoading: false, error: null });
    } catch (err) {
      if (!mountedRef.current) return;
      setState({ meetings: [], isLoading: false, error: (err as Error).message });
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  /**
   * Ana süreç arka planda toplantı değiştirdiğinde (S3 arşivi bitti, bot
   * durumu ilerledi) listeyi sessizce tazele — yükleniyor göstergesi yok.
   * Henüz sunucuya yazılmamış geçici satırlar korunuyor.
   */
  useEffect(() => {
    if (!enabled) return;
    return window.zabit.onMeetingsChanged(() => {
      void window.zabit
        .listMeetings()
        .then((dtos) => {
          if (!mountedRef.current) return;
          setState((prev) => ({
            ...prev,
            meetings: [
              ...prev.meetings.filter((m) => m.id.startsWith('temp-')),
              ...dtos.map(dtoToMeeting),
            ],
          }));
        })
        .catch(() => {});
    });
  }, [enabled]);

  const reportError = useCallback((err: unknown) => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, error: (err as Error).message }));
    void load();
  }, [load]);

  /**
   * Yeni toplantı ekler. Sunucu gerçek kimliği ürettiği için, geçici
   * kimlikli satır dönen kayıtla değiştiriliyor — aksi hâlde sonraki
   * güncelleme var olmayan bir satırı hedefler.
   */
  const addMeeting = useCallback(
    async (draft: Omit<Meeting, 'id'>): Promise<Meeting | null> => {
      const temporaryId = `temp-${Date.now()}`;
      const optimistic: Meeting = { ...draft, id: temporaryId };
      setState((prev) => ({ ...prev, meetings: [optimistic, ...prev.meetings] }));

      try {
        const { id: _ignored, ...dto } = meetingToDto(optimistic);
        const created = await window.zabit.createMeeting(dto);
        if (!mountedRef.current) return null;

        const saved = dtoToMeeting(created);
        setState((prev) => ({
          ...prev,
          meetings: prev.meetings.map((m) => (m.id === temporaryId ? saved : m)),
        }));
        return saved;
      } catch (err) {
        reportError(err);
        return null;
      }
    },
    [reportError]
  );

  /** Toplantıyı klasöre taşır (`null`: klasörden çıkarır). Sürükleme anında görünsün diye iyimser. */
  const moveToFolder = useCallback(
    async (meetingId: string, folderId: string | null) => {
      setState((prev) => ({ ...prev, meetings: prev.meetings.map((m) => (m.id === meetingId ? { ...m, folderId } : m)) }));
      try {
        await window.zabit.folders?.moveMeeting(meetingId, folderId);
      } catch (err) {
        reportError(err);
        throw err;
      }
    },
    [reportError]
  );

  /** Klasör silinince içindeki toplantılar klasörsüz kalır. */
  const forgetFolder = useCallback((folderId: string) => {
    setState((prev) => ({ ...prev, meetings: prev.meetings.map((m) => (m.folderId === folderId ? { ...m, folderId: null } : m)) }));
  }, []);

  const updateMeeting = useCallback(
    async (updated: Meeting) => {
      setState((prev) => ({
        ...prev,
        meetings: prev.meetings.map((m) => (m.id === updated.id ? updated : m)),
      }));

      // Henüz sunucuya yazılmamış geçici satırı güncellemeye çalışma.
      if (updated.id.startsWith('temp-')) return;

      try {
        await window.zabit.updateMeeting(meetingToDto(updated));
      } catch (err) {
        reportError(err);
      }
    },
    [reportError]
  );

  /** Çöp kutusuna taşır; başarılıysa true (geri al bildirimi buna bakar). */
  const deleteMeetings = useCallback(
    async (ids: string[]): Promise<boolean> => {
      setState((prev) => ({
        ...prev,
        meetings: prev.meetings.filter((m) => !ids.includes(m.id)),
      }));

      const persisted = ids.filter((id) => !id.startsWith('temp-'));
      if (persisted.length === 0) return true;

      try {
        await window.zabit.deleteMeetings(persisted);
        return true;
      } catch (err) {
        reportError(err);
        return false;
      }
    },
    [reportError]
  );

  return {
    ...state,
    addMeeting,
    updateMeeting,
    deleteMeetings,
    moveToFolder,
    forgetFolder,
    reload: load,
  };
}
